import { useState } from "react";

import {
  DATE_RANGE_OPTIONS,
  monthWindowForRange,
} from "../../constants/filters";
import { COPY } from "../../copy";
import {
  getAdSalesKpiFromRows,
  getAdSpendKpiFromRows,
  getChartMonthCapacity,
  getConversionsKpiFromRows,
  getFirstYearRoasKpiFromRows,
  getP8AdPerformanceByMonthRows,
  getP8ContinuedSalesSeriesForRange,
  getP8CumulativeFirstYearSeries,
  getP8MonthlyRoasSeries,
  getP8MonthlySalesBySource,
  getP8OverviewLifetimeLabel,
  hasLifetimeMonthCoverage,
} from "../../p8OverviewData";
import type { AdsDateRange, OverviewChartId } from "../../types";
import { adTypesTooltip } from "../AdTypesExplainer";
import { InsightsFilterMenu } from "../InsightsFilterMenu";

import { AdPerformanceByMonthTable } from "./AdPerformanceByMonthTable";
import { ChartMonthNavProvider } from "./chartShared";
import { OverviewChartPair } from "./OverviewCharts";
import { OverviewKpiGrid } from "./OverviewKpiGrid";

type OverviewTabContentProps = {
  onOpenMonth: (monthIndex: number) => void;
  /** Both sides are chosen in the prototype toolbar, one variant group each. */
  leftChart: OverviewChartId;
  rightChart: OverviewChartId;
};

export function OverviewTabContent({
  onOpenMonth,
  leftChart,
  rightChart,
}: OverviewTabContentProps) {
  const [dateRange, setDateRange] = useState<AdsDateRange>("12m");

  // The date range is the only filter on this page: it picks the months, so a
  // three-month range plots three bars. Ad type belongs to the month table and
  // is owned there, which is why nothing above the table is cut by it. Every
  // other series is derived from these points rather than fetched separately,
  // so no two charts can report different numbers for the same month.
  const continuedSales = getP8ContinuedSalesSeriesForRange(dateRange);
  const salesBySource = getP8MonthlySalesBySource(
    continuedSales.map((point) => point.month)
  );
  const cumulativeFirstYear = getP8CumulativeFirstYearSeries(continuedSales);
  const monthlyRoas = getP8MonthlyRoasSeries(continuedSales);
  // Only worth offering when the range is showing less than the advertiser has,
  // and never on the 12-month look-back: a full year is as much as these charts
  // are laid out to hold, so paging would only walk off the range the filter
  // above them says they cover.
  const canPageChartMonths =
    dateRange !== "12m" && getChartMonthCapacity() > continuedSales.length;
  const monthRows = getP8AdPerformanceByMonthRows();
  // The date range scopes the headline metrics only. All four are rolled up from
  // the months it selects, so the card reports the chosen period rather than
  // mixing a scoped metric with a lifetime one.
  const kpiWindow = monthWindowForRange(
    dateRange,
    monthRows[0]?.monthIndex ?? 0
  );
  const kpiMonthRows = kpiWindow
    ? monthRows.filter(
        (row) =>
          row.monthIndex >= kpiWindow.from && row.monthIndex <= kpiWindow.to
      )
    : monthRows;
  // Every card carries its new/reorder split. There is no page-level ad type to
  // narrow them to one, so the split is always both halves of a real total.
  const kpis = [
    {
      ...getAdSalesKpiFromRows(kpiMonthRows, true),
      // The definition names both ad types, so it carries the link to the dialog
      // that defines them. Assembled here rather than in the data layer, which
      // is a .ts file and holds no markup; it supplies the prose, and the link
      // is attached where the card is rendered.
      tooltip: adTypesTooltip(COPY.overview.kpi.totalAdSales),
      interactiveTooltip: true,
    },
    getAdSpendKpiFromRows(kpiMonthRows, true),
    getConversionsKpiFromRows(kpiMonthRows, true),
    getFirstYearRoasKpiFromRows(kpiMonthRows, true),
  ];
  // Offered only when the months on hand actually cover the whole program.
  // Otherwise the roll-up would silently report a trailing window as the
  // lifetime, which reads as a much smaller advertiser than they are.
  const lifetimeAvailable = hasLifetimeMonthCoverage();
  // Or-ed rather than assigned: the study build disables every range but Last
  // 12 months at the source, and overwriting `disabled` here would hand
  // Lifetime back whenever the months happen to cover the whole program.
  const dateRangeOptions = DATE_RANGE_OPTIONS.map((option) =>
    option.value === "lifetime"
      ? { ...option, disabled: option.disabled === true || !lifetimeAvailable }
      : option
  );

  return (
    <div className="space-y-6">
      {/* The date range is the only filter here, and it scopes the whole page.
          Ad type sits on the month table instead: it only ever cut those rows,
          so leaving it up here implied it reached the headline metrics and the
          charts too. */}
      <div className="flex flex-col gap-4 min-[733px]:flex-row min-[733px]:items-start min-[733px]:justify-between">
        <div>
          <h2 className="text-xl leading-8 font-medium text-[#333]">
            {COPY.overview.heading}
          </h2>
          {/* One line, no link. The ad type definitions moved off the end of it
              and onto the ad type filter, which is the one place the two terms
              are a choice the reader has to make. The refresh cadence and
              promotions caveat live down on the month table they describe. */}
          <p className="max-w-[720px] text-sm leading-5 text-[#757575]">
            {COPY.overview.subhead}
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
          <InsightsFilterMenu
            name="overview-date-range"
            labelPrefix="Date range"
            menuTitle="Filter by date range"
            value={dateRange}
            options={dateRangeOptions}
            onChange={setDateRange}
            applyToConfirm
          />
        </div>
      </div>

      <div className="bg-fs-surface-primary rounded-lg border border-[#dfe0e1] p-6">
        <OverviewKpiGrid items={kpis} />
      </div>

      {/* `hasFirstYear` is pinned on: nothing narrows these charts to one ad
          type any more, so the first-year series always have something to
          plot. */}
      <ChartMonthNavProvider enabled={canPageChartMonths}>
        <div className="grid items-stretch gap-6 min-[1366px]:grid-cols-2">
          <OverviewChartPair
            leftChart={leftChart}
            rightChart={rightChart}
            continuedSales={continuedSales}
            salesBySource={salesBySource}
            cumulativeFirstYear={cumulativeFirstYear}
            monthlyRoas={monthlyRoas}
            hasFirstYear
            onOpenMonth={onOpenMonth}
          />
        </div>
      </ChartMonthNavProvider>

      {/* Every month the advertiser has, paginated by the table itself and cut
          by the ad type filter it now owns. The date range stops at the headline
          metrics, so no page filter reaches this list. */}
      <AdPerformanceByMonthTable rows={monthRows} onOpenMonth={onOpenMonth} />

      <p className="text-xs leading-4 text-[#757575]">
        {getP8OverviewLifetimeLabel()}
      </p>
    </div>
  );
}
